const { getOffsetTop } = window.BAO.Utils
const {
  depictProductCard,
  depictFilters,
  formatDepictFilters,
  formatDepictProduct,
} = window.BAO.Depict

class DepictCollection extends window.BAO.CustomElement() {
  connectedCallback () {
    super.connectedCallback()

    this.collectionProducts = []

    this.filters = []
    this.selectedFilters = []
    this.excludedFilters = []
    this.expandedFilters = []

    this.initialLoad = true
    this.isLoadingCollection = false
    this.cursor = null
    this.offset = 0
    this.allProductsLoaded = false

    this.isTicking = false
    this.lastKnownY = window.scrollY

    this.paginationLimit = window.BAO.MediaBreakpoints.isMd
      ? parseInt(this.dataset.desktopLimit, 10)
      : parseInt(this.dataset.mobileLimit, 10)

    this.updateSelectedFiltersFromUrl =
      this.updateSelectedFiltersFromUrl.bind(this)

    this.requestTick = this.requestTick.bind(this)
    this.updateEl = this.updateEl.bind(this)

    this.getCollectionProducts()
    this.getBreadcumbs()
  }

  setupListeners () {
    super.setupListeners()

    this.onInputChange = this.onInputChange.bind(this)
    this.listeners.add(this, 'change', this.onInputChange.bind(this))

    this.handleMoreLessToggle = this.handleMoreLessToggle.bind(this)
    this.listeners.add(this, 'click', this.handleMoreLessToggle.bind(this))

    this.resetAllFilters = this.resetAllFilters.bind(this)
    this.listeners.add(
      this.els['filters-clear'].element,
      'click',
      this.resetAllFilters.bind(this),
    )

    if (this.autoLoadMore) {
      this.onScroll = this.onScroll.bind(this)

      window.addEventListener('scroll', this.onScroll, { passive: true })
    } else {
      this.handleTriggerloadMore = this.handleTriggerloadMore.bind(this)
      this.listeners.add(this, 'click', this.handleTriggerloadMore.bind(this))
    }

    document.addEventListener('depict:load-more', () => {
      this.getCollectionProducts('offset')
    })
  }

  async getCollectionProducts (action = 'default') {
    if (action === 'default') {
      this.cursor = null
      this.offset = 0
      this.allProductsLoaded = false

      const savedPage = parseInt(
        sessionStorage.getItem(
          'collectionScrollPosition-lastVisitedCollectionUrlPage',
        ),
        10,
      )

      if (savedPage > 1 && !this._pageRestored) {
        const baseLimit = this.limit
        const inflatedLimit = baseLimit * savedPage
        this.setAttribute('data-desktop-limit', inflatedLimit)
        this.setAttribute('data-mobile-limit', inflatedLimit)
        this.offset = baseLimit * (savedPage - 1)
        this._pageRestored = true
      }
    }

    if (this.isLoadingCollection) return

    if (!this.collectionExternalId) {
      return
    }

    this.isLoadingCollection = true

    try {
      const url = `https://api.depict.ai/v3/listings/external_id/${this.collectionExternalId}/products`

      // Added top variables to stop line size stopping commits
      const petiteMarkets = window.theme.depict.petiteMarkets
      const marketHandle = window.theme.depict.marketHandle
      const isPetiteRegion = petiteMarkets.includes(marketHandle)
      const petiteFilter =
        window.theme.depict.showPetite && isPetiteRegion
          ? ['true', 'false']
          : ['false']

      const USExclusiveFilter =
        window.theme.depict.marketHandle === 'united-states'
          ? ['true', 'false']
          : ['false']

      const filters = [
        ...this.selectedFilters.map((filter) => ({
          field: filter.key,
          data: filter.values,
          op: filter.op,
        })),
        {
          field: 'app_exclusive',
          data: false,
          op: 'eq',
        },
        {
          field: 'is_petite',
          op: 'in',
          data: petiteFilter,
        },
        {
          field: 'us_exclusive_product',
          op: 'in',
          data: USExclusiveFilter,
        },

        /**
         * If no filters are selected, exclude products that are hidden
         * on the current collection.
         */
        ...(this.selectedFilters.length === 0
          ? [
              {
                field: 'product_hide_on_collections',
                data: this.collectionExternalId,
                op: 'nin',
              },
            ]
          : []),
      ]

      const queryObject = {
        merchant: window.theme.depict.merchant,
        market: window.theme.depict.country.toLowerCase(),
        locale: window.theme.depict.language,
        limit: this.limit,
        cursor: this.cursor,
        filters,
      }

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(queryObject),
      })
      const data = await response.json()

      this.exludeMarketBasedFilters()

      if (this.excludedFilters.length > 0) {
        const { filters: depictFilters = [] } = data

        const filteredFilters = depictFilters.filter(
          (filter) => !this.excludedFilters.includes(filter.field),
        )

        data.filters = filteredFilters
      }

      this.handleCollectionProducts({ data, action })
    } catch (error) {
      throw new Error(error)
    }

    this.isLoadingCollection = false
  }

  exludeMarketBasedFilters () {
    const isUSMarket = window.theme.depict.marketHandle === 'united-states'

    if (isUSMarket) {
      this.excludedFilters.push('garment_size')
    } else {
      this.excludedFilters.push('us_garment_size')
    }
  }

  async handleCollectionProducts (response) {
    const { data, action } = response

    const products = data?.displays

    this.filters = formatDepictFilters(data?.filters, this.selectedFilters)

    if (action === 'offset') {
      // Append new product results to existing array
      this.collectionProducts = [...this.collectionProducts, ...products]
    } else {
      this.collectionProducts = products
    }

    if (this.initialLoad) {
      this.filters?.forEach((filter) => {
        this.updateSelectedFiltersFromUrl(filter)
      })

      if (this.selectedFilters) {
        this.initialLoad = false
        this.isLoadingCollection = false

        this.getCollectionProducts()

        return
      }
    }

    this.cursor = data.cursor

    const filtersHTML = depictFilters({
      filters: this.filters,
      expandedFilters: this.expandedFilters,
      format: 'collection',
    })
    this.els.filters.element.innerHTML = filtersHTML

    const productCardsHTML = this.collectionProducts
      ? await this.getAllProductCardHTML()
      : ''
    this.els['product-grid'].element.innerHTML =
      productCardsHTML ||
      `<li class="prd-List_Item prd-List_Item-empty"><p class="prd-List_Empty fz-14_150">${window.theme.depict.empty}</p></li>`

    this.els.promo?.elements.forEach((promoEl) => {
      const foundIndexEl = document.querySelector(
        `[data-depict-collection-el="item"][data-index="${promoEl.dataset.position}"]`,
      )

      if (foundIndexEl) {
        this.els['product-grid'].element.innerHTML += promoEl.innerHTML
      }
    })

    this.els['filters-close'].element.textContent =
      window.theme.strings.showCountItems.replace('[count]', data?.n_hits)
    this.els['filters-clear'].element.setAttribute(
      'aria-hidden',
      this.selectedFilters.length === 0,
    )

    const fetchedProductCount = products?.length + this.offset
    this.allProductsLoaded = fetchedProductCount >= data?.n_hits

    if (this.autoLoadMore === false) {
      this.els['load-more-container'].element.classList.toggle(
        'clc-ProductGrid_LoadMore-hidden',
        fetchedProductCount >= data?.n_hits,
      )
    }

    this.offset = Number(fetchedProductCount)

    this.initialLoad = false

    if (this._pageRestored) {
      const savedPage = parseInt(
        sessionStorage.getItem(
          'collectionScrollPosition-lastVisitedCollectionUrlPage',
        ),
        10,
      )

      const baseLimit = this.limit / savedPage

      this.setAttribute('data-desktop-limit', baseLimit)
      this.setAttribute('data-mobile-limit', baseLimit)
    }

    document.dispatchEvent(new CustomEvent('depict:ready'))
  }

  async getBreadcumbs () {
    if (!this.collectionExternalId) {
      return
    }

    try {
      const url = new URL(
        `https://api.depict.ai/v3/listings/external_id/${this.collectionExternalId}`,
      )
      const params = new URLSearchParams(url.search)

      const queryObject = {
        merchant: window.theme.depict.merchant,
        market: window.theme.depict.country.toLowerCase(),
        locale: window.theme.depict.language,
      }

      Object.keys(queryObject).forEach((key) => {
        params.set(key, queryObject[key])
      })

      url.search = params.toString()

      // Call the api
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      })

      // Deconstruct the response for the data
      const { ancestors = [], title } = await response.json()

      const breadcrumbsHtml = ancestors.map((ancestor) => {
        return `
          <li class="bdc-Breadcrumb_Item">
            <a class="bdc-Breadcrumb_Link" href="/collections/${ancestor.slug}">
              ${ancestor.title}
            </a>

            <span class="bdc-Breadcrumb_Arrow">
              <svg viewBox="0 0 15 15" xmlns="http://www.w3.org/2000/svg">
                <path d="M8.621 7.492 6.146 5.017l.708-.707 3.182 3.182-3.182 3.182-.708-.708 2.475-2.474Z" fill="currentColor" fill-rule="evenodd" clip-rule="evenodd"/>
              </svg>
            </span>
          </li>
        `
      })

      this.els.breadcrumbs.element.innerHTML = `
        <nav class="bdc-Breadcrumb" aria-label="Breadcrumbs">
          <ul class="bdc-Breadcrumb_Items">
            ${breadcrumbsHtml.filter(Boolean).join('')}
            <li class="bdc-Breadcrumb_Item bdc-Breadcrumb_Item-current">
              <span class="bdc-Breadcrumb_Text">
                ${title}
              </span>
            </li>
          </ul>
        </nav>
        `
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error.message)
    }
  }

  async getAllProductCardHTML () {
    const productCardHTML = await Promise.all(
      this.collectionProducts.map(
        async (productData, index) =>
          await this.getProductCardHTML(productData, index),
      ),
    )
    return productCardHTML.filter(Boolean).join('')
  }

  async getProductCardHTML (productData, index) {
    const formattedProduct = formatDepictProduct(productData)
    const page = Math.ceil((index + 1) / this.paginationLimit)

    return `
      <li class="prd-List_Item" data-index="${index}" data-page="${page}" data-depict-collection-el="item" style="--Order_Desktop: ${index + 1}; --Order_Mobile: ${index + 1};">
        ${depictProductCard({ formattedProduct, index, showFlatImages: this.showFlatImages, disableTag: this.disableTag })}
      </li>`
  }

  onInputChange (event) {
    const changedFilterEl = event.target.closest(
      '[data-depict-collection-el="checkbox"]',
    )

    if (!changedFilterEl) return

    const key = changedFilterEl.dataset.filterKey
    const value = changedFilterEl.dataset.filterOptionValue
    const op = changedFilterEl.dataset.filterOp

    // Check if filter object exists in selected filters
    const currentFilter = this.selectedFilters.find(
      (filter) => filter.key === key,
    )

    if (currentFilter) {
      // Check if checkbox is selected and the value doesn't already exist
      const isChecked = changedFilterEl.checked
      const valueExists = currentFilter.values.includes(value)

      if (isChecked && !valueExists) {
        currentFilter.values.push(value)
        this.updateUrlFilterParams(key, value)
      } else if (!isChecked && valueExists) {
        const valueIndex = currentFilter.values.indexOf(value)
        currentFilter.values.splice(valueIndex, 1)
        this.deleteUrlFilterParams(key, value)

        // Delete filter object from array if no value is selected
        if (!currentFilter.values.length) {
          const filterIndex = this.selectedFilters.indexOf(currentFilter)
          this.selectedFilters.splice(filterIndex, 1)
        }
      }
    } else {
      // Create new filter object in selected filters
      this.selectedFilters.push({
        key,
        values: [value],
        op,
        settings: {
          singleSelect: false,
        },
      })
      this.updateUrlFilterParams(key, value)
    }

    this.getCollectionProducts()
  }

  /**
   * Updates url param with selected filter
   * @param {String} filter - Key of selected filter
   * @param {String} value - Value of selected filter
   */
  updateUrlFilterParams (filter, value) {
    const currentUrl = new URL(window.location)
    const params = new URLSearchParams(currentUrl.search)

    if (params.get(filter)) {
      const currentParams = params.get(filter)
      const updatedParams = [...currentParams.split(','), value].join(',')

      currentUrl.searchParams.set(filter, updatedParams)
    } else {
      currentUrl.searchParams.set(filter, value)
    }

    window.history.replaceState({}, '', currentUrl)
  }

  /**
   * Deletes selected filter from params
   * @param {String} filter - Key of selected filter
   * @param {String} value - Value of selected filter
   */
  deleteUrlFilterParams (filter, value) {
    const currentUrl = new URL(window.location)
    const params = new URLSearchParams(currentUrl.search)

    if (!params.get(filter)) {
      return
    }

    if (value) {
      const currentParams = params.get(filter)
      // remove selected value from params
      const updatedParams = currentParams
        .split(',')
        .filter((param) => param !== value)

      if (updatedParams.length) {
        currentUrl.searchParams.set(filter, updatedParams.join(','))
      } else {
        // delete all filter params if no value is selected
        currentUrl.searchParams.delete(filter)
      }
    } else {
      // delete all filter params if no value is inputed
      currentUrl.searchParams.delete(filter)
    }

    window.history.replaceState({}, '', currentUrl)
  }

  /**
   * Update selected filters from URL parameter on page load.
   * @param {Object} filter - filter object from Depict
   */
  updateSelectedFiltersFromUrl (filter) {
    const currentUrl = new URL(window.location)
    const params = new URLSearchParams(currentUrl.search)

    const currentParams = params.get(filter.key)

    if (!currentParams) {
      return
    }

    const paramOptions = currentParams
      .split(',')
      .map((param) => decodeURIComponent(param))
    const filterArray = [...this.selectedFilters]

    filterArray.push({
      key: filter.key,
      values: paramOptions,
      op: filter.op,
      settings: {
        singleSelect: false,
      },
    })

    this.selectedFilters = filterArray
  }

  /**
   * Clears the current selected filter array
   * @param {String} key - Key value of selected filter.
   */
  resetFilter (key) {
    const filterArray = [...this.selectedFilters]
    const currentFilter = filterArray.find((filter) => filter.key === key)
    const filterIndex = filterArray.indexOf(currentFilter)

    filterArray.splice(filterIndex, 1)
    this.selectedFilters = filterArray

    this.deleteUrlFilterParams(key)
  }

  /**
   * Clears all filters .
   */
  resetAllFilters () {
    const filterArray = [...this.selectedFilters]
    const filterKeys = filterArray.map((filter) => {
      return filter.key
    })

    filterKeys.forEach((key) => {
      this.resetFilter(key)
    })

    this.getCollectionProducts()
  }

  handleMoreLessToggle (event) {
    const clickedTriggerEl = event.target.closest(
      '[data-depict-collection-el="more-toggle"]',
    )
    if (!clickedTriggerEl) return

    const matchedGroupEl = this.querySelector(
      `[data-depict-collection-el="group"][data-key="${clickedTriggerEl.dataset.key}"]`,
    )

    const currentState = matchedGroupEl.classList.contains(
      'drw-Filters_Group-expanded',
    )

    matchedGroupEl.classList.toggle(
      'drw-Filters_Group-expanded',
      !currentState,
    )

    if (currentState) {
      const currentFilter = this.expandedFilters.find(
        (filterGroup) => filterGroup.key === clickedTriggerEl.dataset.key,
      )
      const filterIndex = this.expandedFilters.indexOf(currentFilter)
      this.expandedFilters.splice(filterIndex, 1)
    } else {
      this.expandedFilters.push({
        key: clickedTriggerEl.dataset.key,
      })
    }
  }

  handleTriggerloadMore (event) {
    const triggerEl = event.target.closest(
      '[data-depict-collection-el="load-more"]',
    )

    if (!triggerEl) return

    this.paginationLimit = this.limit

    this.setAttribute('data-mobile-limit', 60)
    this.setAttribute('data-desktop-limit', 60)
    this.setAttribute('data-auto-load-more', 'true')

    this.els['load-more-container'].element.classList.toggle(
      'clc-ProductGrid_LoadMore-hidden',
      true,
    )

    this.getCollectionProducts('offset')

    this.onScroll = this.onScroll.bind(this)

    window.addEventListener('scroll', this.onScroll, { passive: true })
  }

  onScroll () {
    if (this.allProductsLoaded) return

    this.lastKnownY = window.scrollY

    this.requestTick()
  }

  requestTick () {
    if (!this.isTicking) requestAnimationFrame(this.updateEl)

    this.isTicking = true
  }

  updateEl () {
    this.isTicking = false

    this.handleScroll()
  }

  handleScroll () {
    const productGridEnd = getOffsetTop(this) + this.offsetHeight
    const loadMoreTriggerPoint = productGridEnd - window.innerHeight * 1.5

    if (
      loadMoreTriggerPoint < this.lastKnownY &&
      this.isLoadingCollection === false
    ) {
      this.getCollectionProducts('offset')
    }
  }

  get collectionExternalId () {
    return this.collectionHandle
  }

  get collectionId () {
    return this.dataset.collectionId
  }

  get collectionHandle () {
    return this.dataset.collectionHandle
  }

  get limit () {
    return window.BAO.MediaBreakpoints.isMd
      ? parseInt(this.dataset.desktopLimit, 10)
      : parseInt(this.dataset.mobileLimit, 10)
  }

  get autoLoadMore () {
    return this.dataset.autoLoadMore === 'true'
  }

  get showFlatImages () {
    return this.dataset.flatImages === 'true'
  }

  get disableTag () {
    return this.dataset.disableTag
  }
}

if (!customElements.get('depict-collection')) {
  customElements.define('depict-collection', DepictCollection)
}
